import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { StatCard } from '@nucleus-commerce/admin-kit/layout';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Card } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { useStore } from '@/shared/store/store-context';

import { RangePicker } from '../components/range-picker';
import { ReportExportButton } from '../components/report-export-button';
import { usePaymentsReport } from '../hooks/use-payments-report';
import { useReportRange } from '../hooks/use-report-range';

function PaymentsReport() {
  const { t } = useTranslation('reports');
  const store = useStore();
  const format = useFormat();
  const { range, setRange } = useReportRange();
  const query = usePaymentsReport(store.id, range);
  const report = query.data;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
        <RangePicker range={range} onChange={setRange} />
        <ReportExportButton storeId={store.id} report="payments" range={range} />
      </div>
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2" data-tour="reports.payments.tiles">
            <div role="group" aria-label={t('payments.refunds')} className="contents">
              <StatCard
                label={t('payments.refunds')}
                value={report ? format.money(report.refunds.amount) : '—'}
                detail={report ? t('payments.refundCount', { count: report.refunds.count }) : ''}
                isLoading={query.isPending}
              />
            </div>
            <div role="group" aria-label={t('payments.unpaid')} className="contents">
              <StatCard
                label={t('payments.unpaid')}
                value={report ? format.money(report.unpaid.amount) : '—'}
                detail={report ? t('payments.unpaidCount', { count: report.unpaid.count }) : ''}
                tone={report && report.unpaid.count > 0 ? 'warning' : 'neutral'}
                isLoading={query.isPending}
              />
            </div>
          </div>
          <Card className="gap-0 overflow-hidden py-0" data-tour="reports.payments.methods">
            <Table aria-label={t('payments.methods')}>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">{t('columns.method')}</TableHead>
                  <TableHead className="text-right">{t('columns.orders')}</TableHead>
                  <TableHead className="text-right">{t('columns.gross')}</TableHead>
                  <TableHead className="text-right">{t('columns.paid')}</TableHead>
                  <TableHead className="pr-5 text-right">{t('columns.outstanding')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.isPending ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={5} className="px-5">
                        <Skeleton className="h-5" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : report && report.methods.length > 0 ? (
                  report.methods.map((method) => (
                    <TableRow key={method.key}>
                      <TableCell className="pl-5 font-medium">{method.name}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {format.number(method.ordersCount)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {format.money(method.gross)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {format.money(method.paid)}
                      </TableCell>
                      <TableCell className="pr-5 text-right tabular-nums">
                        {format.money(method.outstanding)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-muted-foreground h-24 text-center">
                      {t('empty.noSales')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}

export function PaymentsReportPage() {
  return (
    <FeatureErrorBoundary name="reports.payments">
      <PaymentsReport />
    </FeatureErrorBoundary>
  );
}
